/* ============================================================
   Production PDP wiring for the Formula Calculator.
   Unlike js/ui.js (the dev/test harness, which lets you pick ANY
   formula type + product from dropdowns), this version is driven
   entirely by the current product page: window.FormulaCalculatorSku
   (the page's base SKU, set inline by the Liquid snippet) is looked
   up in a small manifest (also inlined by Liquid) to get
   {formulaKind, productKey, stageKey} - no dropdowns, no picking.

   Heavy assets (the full powder+rtf+prices dataset, ~500KB) are
   fetched lazily on first click, not on every page load - most PDPs
   never open the calculator, so there's no reason to ship that to
   everyone up front. FormulaEngine and Pricing (engine.js, pricing.js)
   are loaded eagerly since they're small and shared with the dev
   build unchanged.
   ============================================================ */

(function () {
  const state = {
    powderData: null,
    rtfData: null,
    formulaKind: null,
    productKey: null,
    ageMonths: 5,
    feedsPerDay: 6,
    weeksOfFormula: 8,
    dataLoaded: false,
    dataLoadPromise: null,
  };

  // Availability check lives in js/stock-check.js; if that file did not load,
  // the calculator simply skips the pre-check (Shopify still refuses what
  // isn't there and the add-to-cart rolls back).
  const Stock = window.FormulaCalculatorStock || {
    findStockProblems: async () => [],
    showStockProblem() {},
    addPlanToCart: async () => alert("Sorry, the calculator could not finish loading. Please refresh the page."),
  };
  const { showStockProblem } = Stock;
  const variantLabels = {};
  const findStockProblems = (lineItems, fresh) => Stock.findStockProblems(lineItems, fresh, variantLabels);

  function manifestEntry() {
    const sku = window.FormulaCalculatorSku;
    const manifest = window.FormulaCalculatorManifest || {};
    return sku ? manifest[sku] : null;
  }

  // The prices inside the bundled dataset are a snapshot and go stale as soon
  // as a price changes in Shopify, which would make the plan prices and the
  // "Save" amount differ from the cart. So the prices of this product's stages
  // are overwritten with the live ones from the storefront product feeds
  // (price in cents, in the shop currency). The snapshot stays as fallback
  // when a feed can't be read, and is kept when the shopper browses in
  // another currency, where the feeds show converted prices.
  // Compare-at price of each stage's single-unit variant, from the same live
  // feeds. The cart's "Savings" is the compare-at of the lines in the cart
  // (these single-unit variants) minus the cart total, so the plan's "Save"
  // amount uses the same baseline.
  const unitCompareAt = {};

  // Main product image of each stage (stage key -> image URL), from the same
  // live product feeds. Shown on the plan cards; the text placeholder stays
  // when a feed or image is unavailable.
  const stageImages = {};

  async function refreshLivePrices(bundle) {
    const entry = manifestEntry();
    if (!entry) return;
    const shopCurrency = window.FormulaCalculatorCurrency;
    const active = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    if (shopCurrency && active && active !== shopCurrency) return;

    const product = bundle[entry.formulaKind].products[entry.productKey];
    const unitIds = new Set(Object.values(product.stages).map((s) => String(s.cartUnitVariantId)));
    const handleOf = Stock.stageHandles || {};
    const handles = [...new Set(Object.values(product.stages).map((s) => handleOf[s.cartUnitVariantId]).filter(Boolean))];
    await Promise.all(
      handles.map(async (handle) => {
        try {
          const res = await fetch(`/products/${handle}.js`);
          if (!res.ok) return;
          const live = await res.json();
          const imageUrl = live.featured_image || (live.images && live.images[0]);
          if (imageUrl) {
            const sized = imageUrl + (imageUrl.indexOf("?") === -1 ? "?" : "&") + "width=240";
            new Image().src = sized; // preload, so the plan cards show it straight away
            Object.entries(product.stages).forEach(([stageKey, stage]) => {
              if (handleOf[stage.cartUnitVariantId] === handle) stageImages[stageKey] = sized;
            });
          }
          live.variants.forEach((v) => {
            if (unitIds.has(String(v.id))) unitCompareAt[String(v.id)] = v.compare_at_price ? v.compare_at_price / 100 : null;
            const priced = bundle.prices[String(v.id)];
            if (!priced) return;
            priced.price = v.price / 100;
            priced.compareAtPrice = v.compare_at_price ? v.compare_at_price / 100 : null;
            priced.availableForSale = v.available;
          });
        } catch (e) {
          console.warn("Formula calculator: live prices unavailable for", handle, e);
        }
      })
    );
  }

  async function ensureDataLoaded() {
    if (state.dataLoaded) return;
    if (!state.dataLoadPromise) {
      state.dataLoadPromise = (async () => {
        // The dosing+pricing bundle (~470KB) is served as several small
        // plain-text parts (see build-theme-assets.py) rather than one
        // file - fetch them in parallel and concatenate in order before
        // parsing, since Promise.all preserves input order regardless of
        // which request resolves first.
        const urls = window.FormulaCalculatorDataUrls || [];
        const parts = await Promise.all(urls.map((u) => fetch(u).then((r) => r.text())));
        const bundle = JSON.parse(parts.join(""));
        state.powderData = bundle.powder;
        state.rtfData = bundle.rtf;
        // pricing.js's loadPrices() reads window.__BUNDLED_DATA__.prices
        // directly (same convention the standalone build uses) rather than
        // fetching data/prices.json separately.
        window.__BUNDLED_DATA__ = bundle;
        await Pricing.loadPrices();
        await refreshLivePrices(bundle);
        state.dataLoaded = true;
      })();
    }
    await state.dataLoadPromise;
  }

  function currentDataset() {
    return state.formulaKind === "powder" ? state.powderData : state.rtfData;
  }

  function currentProduct() {
    const dataset = currentDataset();
    return dataset.products[state.productKey];
  }

  // Some brand names already end with their own type word (e.g. brand
  // "Kendamil Goat", type "Goat") - appending the type there would read
  // "Kendamil Goat Goat". Drop it when it's just a repeat of the end of
  // the brand name. (Kept in sync with js/ui.js.)
  function typeAlreadyInBrand(brand, readableType) {
    return brand.toLowerCase().endsWith(readableType.toLowerCase());
  }

  function productSubtitle(product) {
    const readableType = product.type.replace(/\bHA\b/, "HypoAllergenic");
    return typeAlreadyInBrand(product.brand, readableType) ? product.brand : `${product.brand} ${readableType}`;
  }

  function updateModalSubtitle() {
    document.getElementById("fc-modal-subtitle").textContent = productSubtitle(currentProduct());
  }

  function productNeedsStageLabel(product) {
    const stageKeys = Object.keys(product.stages);
    return !(stageKeys.length === 1 && /stage/i.test(stageKeys[0]));
  }

  function resultsSubtitle(product, stageOrder) {
    let readableType = product.type.replace(/\bHA\b/, "HypoAllergenic");
    if (!productNeedsStageLabel(product) || !stageOrder || stageOrder.length === 0) {
      return typeAlreadyInBrand(product.brand, readableType) ? product.brand : `${product.brand} ${readableType}`;
    }
    const lastStage = stageOrder[stageOrder.length - 1];
    const stageText = stageOrder.length === 1 ? `Stage ${stageOrder[0]}` : `Stage ${stageOrder[0]}–${lastStage}`;
    readableType = readableType.replace(/\bStage\s+(Pre|\d+)\b\s*/gi, " ").replace(/\s+/g, " ").trim();
    readableType = readableType.replace(/\s*\bPre\b\s*$/i, "");
    // Later stages continue in the regular products, so "with teats" only
    // describes a plan that stays in stage 1.
    if (stageOrder.length > 1) readableType = readableType.replace(/\s*\bwith teats\b/i, "");
    const suffix = readableType && !typeAlreadyInBrand(product.brand, readableType) ? ` ${readableType}` : "";
    return `${product.brand} ${stageText}${suffix}`;
  }

  function setupStepper(prefix, min, max, onChange) {
    const valueEl = document.getElementById(`${prefix}-value`);
    const minusBtn = document.getElementById(`${prefix}-minus`);
    const plusBtn = document.getElementById(`${prefix}-plus`);

    function render(value) {
      valueEl.textContent = value;
    }

    minusBtn.addEventListener("click", () => {
      const next = Math.max(min, onChange.get() - 1);
      onChange.set(next);
      render(next);
    });

    plusBtn.addEventListener("click", () => {
      const next = Math.min(max, onChange.get() + 1);
      onChange.set(next);
      render(next);
    });

    render(onChange.get());
  }

  function suggestFeedsPerDay() {
    const product = currentProduct();
    const suggestion = FormulaEngine.suggestFeedsPerDay(product, state.ageMonths);
    if (suggestion !== null) {
      state.feedsPerDay = suggestion;
      document.getElementById("fc-feeds-value").textContent = suggestion;
    }
  }

  function stageLabel(stageName) {
    return /stage/i.test(stageName) ? stageName : `Stage ${stageName}`;
  }

  function stageImageBox(stageName, showLabel) {
    const src = stageImages[stageName];
    if (src) {
      return `<div class="fc-stage-img-box"><img src="${src}" alt="${stageLabel(stageName)}"></div>`;
    }
    return `<div class="fc-stage-img-box">${showLabel ? stageLabel(stageName) + "<br>" : ""}image</div>`;
  }

  function renderCard({ label, isBestValue, weeksCovered, unitsByStage, orderedStages, totalPrice, baselinePrice, unitLabel, product }) {
    const stageNames = orderedStages.filter((s) => unitsByStage[s] > 0);
    const totalUnits = stageNames.reduce((sum, s) => sum + unitsByStage[s], 0);
    const pricePerUnit = totalUnits > 0 ? totalPrice / totalUnits : 0;
    const savings = baselinePrice - totalPrice;
    const needsStageLabel = productNeedsStageLabel(product);

    const imagesRow = stageNames.length
      ? `<div class="fc-stage-images">${stageNames
          .map(
            (s) => `
        <div>
          ${stageImageBox(s, needsStageLabel)}
          <div class="fc-stage-units">${unitsByStage[s]} ${unitLabel}${unitsByStage[s] === 1 ? "" : "s"}</div>
          ${needsStageLabel ? `<div class="fc-stage-name">${stageLabel(s)}</div>` : ""}
        </div>`
          )
          .join("")}</div>`
      : "";

    return `
      <div class="fc-card ${isBestValue ? "best-value" : ""}">
        ${imagesRow}
        <div class="fc-plan-label ${isBestValue ? "best-value-label" : ""}">${label}</div>
        <div class="fc-weeks-line">&asymp; ${weeksCovered} weeks supply</div>
        <div class="fc-price-row">
          <div class="fc-total-price">${window.FormulaCalculatorCurrency || "CAD"} $${totalPrice.toFixed(2)}</div>
          ${savings > 0.01 ? `<div class="fc-save-badge">Save $${savings.toFixed(2)}</div>` : ""}
        </div>
        <div class="fc-per-unit-price">${window.FormulaCalculatorCurrency || "CAD"} $${pricePerUnit.toFixed(2)}/${unitLabel}</div>
        <div class="fc-stock-warning" role="alert" hidden></div>
        <button type="button" class="fc-add-to-cart-btn" data-plan="${isBestValue ? "bestValue" : "exact"}">Add complete plan to cart</button>
      </div>
    `;
  }

  // Same "single real variant per stage, quantity-exact" cart composition
  // as js/ui.js's buildOptionForCart - the cart-wide volume-discount
  // Function applies the real staffel price on top once it's live.
  function buildOptionForCart(unitsByStage, product, stageOrder, planLabel) {
    const stagesWithUnits = stageOrder.filter((s) => unitsByStage[s] > 0).map((s) => [s, unitsByStage[s]]);
    const grandTotalUnits = stagesWithUnits.reduce((sum, [, units]) => sum + units, 0);

    const referenceStage = stagesWithUnits[0]?.[0];
    const tierUnitsAscending = referenceStage
      ? [...product.stages[referenceStage].tiers].sort((a, b) => a.units - b.units).map((t) => t.units)
      : [];
    const bracketIndex = FormulaEngine.findTierBracketIndex(grandTotalUnits, tierUnitsAscending);

    const lineItems = [];
    const purchasedUnitsByStage = {};
    let totalPrice = 0;
    let baselinePrice = 0;

    for (const [stage, units] of stagesWithUnits) {
      const stageDef = product.stages[stage];
      const pricedTiersAscending = Pricing.attachPricing(stageDef.tiers).sort((a, b) => a.units - b.units);
      const rateAtBracket = pricedTiersAscending[Math.min(bracketIndex, pricedTiersAscending.length - 1)];
      const unitCompare = unitCompareAt[String(stageDef.cartUnitVariantId)];
      const compareAtPerUnit = unitCompare
        ? unitCompare
        : rateAtBracket.compareAtPrice
        ? rateAtBracket.compareAtPrice / rateAtBracket.units
        : rateAtBracket.pricePerUnit;

      purchasedUnitsByStage[stage] = units;
      totalPrice += units * rateAtBracket.pricePerUnit;
      baselinePrice += units * compareAtPerUnit;
      if (stageDef.cartUnitVariantId) {
        variantLabels[stageDef.cartUnitVariantId] = productNeedsStageLabel(product) ? stageLabel(stage) : "";
        // Underscore-prefixed properties are hidden from the customer-facing
        // cart/checkout UI but stay on the order (admin + Orders API), so
        // orders placed via the calculator - and their value - can be
        // reported on without showing internal tags to the customer.
        lineItems.push({
          id: Number(stageDef.cartUnitVariantId),
          quantity: units,
          properties: {
            _added_via: "formula_calculator",
            _fc_plan: planLabel,
          },
        });
      }
    }

    const orderedStages = stagesWithUnits.map(([s]) => s);
    return { lineItems, totalPrice, baselinePrice, purchasedUnitsByStage, orderedStages, grandTotalUnits, bracketIndex };
  }

  function calculate() {
    const product = currentProduct();
    const unitLabel = state.formulaKind === "powder" ? "can" : "bottle";
    const params = {
      ageMonths: state.ageMonths,
      feedsPerDay: state.feedsPerDay,
      weeksOfFormula: state.weeksOfFormula,
    };

    const exact = FormulaEngine.computePlan(product, state.formulaKind, params);
    const resultsEl = document.getElementById("fc-results");

    if (!exact.endStage) {
      resultsEl.innerHTML = `
        <button type="button" class="fc-edit-answers-link">&larr; Edit answers</button>
        <div class="fc-no-data-note">No feeding data available for this age range - please consult your doctor for very young infants.</div>
      `;
      document.querySelector(".fc-modal").classList.add("fc-showing-results");
      resultsEl.querySelector(".fc-edit-answers-link").addEventListener("click", () => {
        document.querySelector(".fc-modal").classList.remove("fc-showing-results");
      });
      return;
    }

    const exactCart = buildOptionForCart(exact.unitsByStage, product, exact.stageOrder, "exact");

    const currentTotalUnits = Object.values(exactCart.purchasedUnitsByStage).reduce((a, b) => a + b, 0);
    const tierUnits = product.stages[exact.endStage].tiers.map((t) => t.units).sort((a, b) => a - b);
    const nextBreakpoint = tierUnits.find((u) => u > currentTotalUnits);

    // "Best value" only makes sense when it actually reaches the next price
    // tier. The feeding tables end at a fixed week, so for older babies the
    // extension can run out of data first and would just repeat the exact plan.
    const bestValueCandidate = nextBreakpoint !== undefined
      ? FormulaEngine.computeBestValueOption(product, state.formulaKind, params, exact, nextBreakpoint)
      : null;
    const bestValue = bestValueCandidate && bestValueCandidate.reachedTarget ? bestValueCandidate : null;

    const exactCard = renderCard({
      label: "Your exact plan",
      isBestValue: false,
      weeksCovered: exact.weeksCovered,
      unitsByStage: exactCart.purchasedUnitsByStage,
      orderedStages: exactCart.orderedStages,
      totalPrice: exactCart.totalPrice,
      baselinePrice: exactCart.baselinePrice,
      unitLabel,
      product,
    });

    let bestValueCard = "";
    let bvCart = null;
    if (bestValue) {
      bvCart = buildOptionForCart(bestValue.unitsByStage, product, bestValue.stageOrder, "bestValue");
      bestValueCard = renderCard({
        label: "Best value",
        isBestValue: true,
        weeksCovered: bestValue.weeksCovered,
        unitsByStage: bvCart.purchasedUnitsByStage,
        orderedStages: bvCart.orderedStages,
        totalPrice: bvCart.totalPrice,
        baselinePrice: bvCart.baselinePrice,
        unitLabel,
        product,
      });
    }

    resultsEl.innerHTML = `
      <button type="button" class="fc-edit-answers-link">&larr; Edit answers</button>
      <h2>Your Formula Plan</h2>
      <div class="fc-results-subtitle">${resultsSubtitle(product, bestValue ? bestValue.stageOrder : exact.stageOrder)}</div>
      <div class="fc-cards">${exactCard}${bestValueCard}</div>
    `;

    document.querySelector(".fc-modal").classList.add("fc-showing-results");

    resultsEl.querySelector(".fc-edit-answers-link").addEventListener("click", () => {
      document.querySelector(".fc-modal").classList.remove("fc-showing-results");
    });

    resultsEl.querySelectorAll(".fc-add-to-cart-btn").forEach((btn) => {
      const isBestValuePlan = btn.dataset.plan === "bestValue";
      const cart = isBestValuePlan ? bvCart : exactCart;
      btn.addEventListener("click", () => Stock.addPlanToCart(cart.lineItems, btn, variantLabels));
      // Tell the customer up front when this plan can't be fulfilled, rather
      // than only after pressing the button (which re-checks anyway).
      findStockProblems(cart.lineItems, false).then((problems) => {
        if (problems.length) showStockProblem(btn, problems);
      });
    });
  }

  async function openModal(triggerBtn) {
    const originalText = triggerBtn.innerHTML;
    triggerBtn.disabled = true;
    try {
      await ensureDataLoaded();
    } catch (e) {
      triggerBtn.disabled = false;
      triggerBtn.innerHTML = originalText;
      console.error("Formula calculator failed to load:", e);
      return;
    }
    triggerBtn.disabled = false;
    triggerBtn.innerHTML = originalText;

    const entry = manifestEntry();
    state.formulaKind = entry.formulaKind;
    state.productKey = entry.productKey;
    updateModalSubtitle();
    suggestFeedsPerDay();

    document.getElementById("fc-modal-overlay").hidden = false;
    document.body.style.overflow = "hidden";
  }

  function closeModal() {
    document.getElementById("fc-modal-overlay").hidden = true;
    document.body.style.overflow = "";
  }

  // The theme renders the "For Babies..."/"NNN Grams of powder" lines as
  // two separate, independently-configured urgency blocks with no shared
  // wrapper (they're generic reusable blocks, not something specific to
  // this product template) - so to sit the trigger beside them on desktop
  // without touching that shared snippet, we regroup the DOM at runtime:
  // find the two urgency divs immediately preceding our block (skipping
  // the <style>/<script>/<link> nodes the theme/Liquid sprinkle between
  // them) and wrap all three in a flex row. The CSS only turns that row
  // into a flex layout above the mobile breakpoint, so if the structure
  // doesn't match (different block order, a theme change) this just
  // no-ops and the page falls back to the original stacked layout.
  function regroupInfoRow(triggerBtn) {
    const block = triggerBtn.closest(".formula-calculator-block");
    if (!block) return;
    const urgencyEls = [];
    let node = block.previousElementSibling;
    while (node && urgencyEls.length < 2) {
      if (node.classList && node.classList.contains("product__urgency")) {
        urgencyEls.unshift(node);
      } else if (node.tagName !== "STYLE" && node.tagName !== "SCRIPT" && node.tagName !== "LINK") {
        break;
      }
      node = node.previousElementSibling;
    }
    if (urgencyEls.length !== 2) return;

    const row = document.createElement("div");
    row.className = "fc-info-row";
    const col = document.createElement("div");
    col.className = "fc-info-col";

    urgencyEls[0].parentNode.insertBefore(row, urgencyEls[0]);
    urgencyEls.forEach((el) => col.appendChild(el));
    row.appendChild(col);
    row.appendChild(block);
  }

  function init() {
    const entry = manifestEntry();
    const triggerBtn = document.getElementById("formula-calculator-trigger");
    if (!triggerBtn) return;
    if (!entry) {
      // Not a product this calculator covers (sachets, short-date-sale
      // duplicates, non-formula products, ...) - hide the trigger entirely
      // rather than open a modal with nothing to calculate.
      triggerBtn.remove();
      return;
    }

    regroupInfoRow(triggerBtn);

    // The popup is rendered inside the product block, whose ancestors can form
    // their own stacking context (the theme's sticky header and product tabs
    // then painted over the popup while scrolling). Moving the overlay to the
    // end of <body> makes its z-index count against the whole page.
    const overlay = document.getElementById("fc-modal-overlay");
    if (overlay && overlay.parentNode !== document.body) document.body.appendChild(overlay);

    setupStepper("fc-age", 0, 36, {
      get: () => state.ageMonths,
      set: (v) => {
        state.ageMonths = v;
        suggestFeedsPerDay();
      },
    });
    setupStepper("fc-feeds", 1, 12, {
      get: () => state.feedsPerDay,
      set: (v) => (state.feedsPerDay = v),
    });
    setupStepper("fc-weeks", 1, 52, {
      get: () => state.weeksOfFormula,
      set: (v) => (state.weeksOfFormula = v),
    });

    document.getElementById("fc-calculate-btn").addEventListener("click", calculate);

    triggerBtn.addEventListener("click", () => openModal(triggerBtn));
    document.getElementById("fc-modal-close").addEventListener("click", closeModal);
    document.getElementById("fc-modal-overlay").addEventListener("click", (e) => {
      if (e.target === document.getElementById("fc-modal-overlay")) closeModal();
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && !document.getElementById("fc-modal-overlay").hidden) closeModal();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
